<template>
  <div v-if="isOpen" id="control-ai-chat">
    <ai-toolkit-chat-card
      ref="cardRef"
      :heading="config.title ?? dependencies.identity.agentName"
      :disclaimer="dependencies.identity.disclaimer"
      :empty-heading="dependencies.identity.emptyStateTitle"
      :empty-description="dependencies.identity.emptyStateBody"
      trap-focus
      @prompt-select="handlePromptSelect"
      @close="handleClose"
      @collapsed-change="handleCollapsedChange"
      @invoke-end="handleInvokeEnd"
      @invoke-error="handleInvokeError"
      @open-survey="handleOpenSurvey"
      @feedback-submit="handleFeedbackSubmit"
      @feedback-dismiss="handleFeedbackDismiss"
      @visible-change="handleFeedbackCtaVisibleChange"
      @feedback-survey-close="handleFeedbackSurveyClose"
      @feedback="handleFeedback"
      @rating-error="handleRatingError"
      @copied="handleCopied"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, onUnmounted } from 'vue'
import {
  clearPersistedConversation,
  countUserTurns,
  createAnalyticsTracker,
  type Agent,
  type AgentRouter,
  type ConversationStore,
  type MessageUser,
  type PersistConversationOptions,
} from '@onxmaps/ai-toolkit'
import '@onxmaps/ai-toolkit/components/ai-toolkit-chat-card'
import {
  defaultToolResultRenderers,
  type AiToolkitChatCard,
  type ChatCardAdapters,
  type ChatCardFeedbackOptions,
  type ChatCardSurveyOptions,
  type ChatCardToolResultRenderers,
} from '@onxmaps/ai-toolkit/components/ai-toolkit-chat-card'
import type { FeedbackSubmission } from '@onxmaps/ai-toolkit/components/ai-toolkit-feedback-survey'
import {
  attachDevtoolsBridge,
  type DevtoolsHandle,
} from '@onxmaps/ai-toolkit/devtools'
import { DEFAULT_SUPPORT_URL } from '@onxmaps/ai-toolkit/toolbox/definitions/appSupport'
import {
  GET_LAYERS_NAME,
  TOGGLE_LAYERS_NAME,
} from '@onxmaps/ai-toolkit/toolbox/definitions/mapSetLayers'
import type { AiDependencies } from '@/lib/ai/lib/dependencies'
import { reportAiError } from '@/lib/ai/lib/report'
import { useAiChatCard } from '@/lib/ai/components/use-ai-chat-card'
import { getApolloClient } from '@/lib/apollo-client'
import { isDaily, isStaging } from '@/lib/environment'
import marketingEventService, {
  type EventData,
} from '@/services/marketing-event-service'
import { goToPurchase } from '@/lib/purchase-utils'
import { PURCHASE_CTA_ORIGINS } from '@/lib/enums-marketing'
import {
  resetMessageContext,
  hydrateMessageContext,
  snapshotMessageContext,
} from '@/lib/ai/context/message'

// Feedback-engagement session gating (was `ai-chat-feedback-cta.vue`'s
// `ENGAGEMENT_STORAGE_KEY`) — kept the same so a user who already dismissed
// or submitted feedback this session doesn't see the CTA reappear.
const FEEDBACK_ENGAGEMENT_KEY = 'onx-guide.feedback-cta-engaged'

// Vertical-specific implementations injected by the host that mounts this
// component. Keeps `lib/ai` portable: the concrete adapters live outside it
// (e.g. app/lib/ai) and flow in here as data.
const props = defineProps<{ dependencies: AiDependencies }>()

const {
  config,
  isOpen,
  openEventKey,
  expandKey,
  conversationKey,
  pendingPrompt,
  pendingMessage,
  consumePendingPrompt,
  consumePendingMessage,
  closeAiChatCard,
} = useAiChatCard()

const CONVERSATION_STORAGE_KEY = 'onx-guide-conversation'
const MESSAGE_CONTEXT_STORAGE_KEY = 'onx-guide-conversation-context'

// Runs once, when this (singleton) component mounts — before `card.persistence`
// (assigned later, in the cardRef watch) has a chance to hydrate a stored
// conversation. Restores the message-context differ's baseline from the same
// sessionStorage tier the conversation itself persists to, so a restored
// conversation's next message reports only real deltas instead of a spurious
// first-message re-baseline. Only restores when a conversation snapshot is
// actually present: the two keys are written independently (this one from
// `handleInvokeEnd`, the conversation's from the card's own coalesced flush),
// so a conversation write that failed (quota, etc.) must not leave a context
// baseline for a conversation that isn't there — that would suppress context
// the (nonexistent) restored conversation never actually received.
hydrateMessageContext(
  sessionStorage.getItem(CONVERSATION_STORAGE_KEY)
    ? sessionStorage.getItem(MESSAGE_CONTEXT_STORAGE_KEY)
    : null
)

// The toolkit publishes `Record<string, unknown>`; the host's marketing
// event service takes the narrower `EventData` — the cast bridges the two.
const analytics = createAnalyticsTracker((name, payload) =>
  marketingEventService.publish(name, payload as EventData)
)

// The toolkit's `persistConversation` fires `onError` on every failed write
// (e.g. a streaming turn hitting a full quota repeatedly) — latch to a
// single reported exception so a persistent failure never spams Sentry.
let persistenceErrorReported = false
const persistence: PersistConversationOptions = {
  name: CONVERSATION_STORAGE_KEY,
  onError: (error) => {
    if (!persistenceErrorReported) {
      persistenceErrorReported = true
      reportAiError(error, { surface: 'persistence', layer: 'host' })
    }
  },
}

// The card creates and owns its own store; assigning `persistence` (below,
// in the cardRef watch) hydrates it from any stored snapshot. Note: a
// snapshot written by the pre-0.44.0 local persistence module has no
// `version` field and fails the toolkit's `isValidSnapshot` check, so it's
// silently discarded on first hydration — a self-healing, one-time no-op.
const cardRef = ref<AiToolkitChatCard | null>(null)

// Plain (non-reactive) mirror of `cardRef.value`, kept in sync by the watch
// below. Vue nulls out template refs to a component's children as part of
// tearing down that subtree, *before* running the parent's own `onUnmounted`
// callbacks -- so by the time `onUnmounted` below runs, `cardRef.value` is
// already null and `cardRef.value?.abort()` would silently no-op, leaving
// an in-flight request (e.g. a streaming turn) running past unmount. This
// mirror is unaffected by that ordering, since it isn't a template ref.
let mountedCard: AiToolkitChatCard | null = null

// The card ships built-in renderers for the other standard toolbox tools
// (find-places, weather, support, upgrade, and mapToggleLayers) once
// `adapters.map.getLayer` is set below, so only mapGetLayers needs an
// explicit override here: the toolkit intentionally ships no default for it
// (RFC-0007 §6.22.1 — a host that wants interactive get-layers cards
// registers its own; upstream ai-toolkit#590 asks for a cleaner exported
// factory so hosts stop reaching into the internal defaults map). Reusing
// the toggle-layers renderer is safe because `GetLayersStructuredContent`
// (`{ applied, skipped }`) is a subset of `ToggleLayersStructuredContent`'s
// shape, and the renderer only reads `.applied`.
const renderers: ChatCardToolResultRenderers = {
  [GET_LAYERS_NAME]: defaultToolResultRenderers(() => adapters.map)[
    TOGGLE_LAYERS_NAME
  ],
}

/**
 * Injects any host-supplied `citationStyles` into the card's shadow root
 * exactly once per mount. A plain `<style>` element here (rather than
 * trying to reach in from outside) is what lets it use ordinary selectors
 * against `ai-toolkit-markdown`'s rendered content at all -- that content is
 * light DOM, but nested inside the *card's* shadow root, so an external
 * stylesheet can't reach it (only CSS custom properties and `::part()`
 * cross a shadow boundary, and neither covers "every markdown node"). A host
 * with nothing to style can simply not supply `citationStyles`.
 */
function injectCitationStyles(shadowRoot: ShadowRoot): void {
  if (!props.dependencies.citationStyles) {
    return
  }
  const style = document.createElement('style')
  style.textContent = props.dependencies.citationStyles
  shadowRoot.appendChild(style)
}

let agentPromise: Promise<Agent> | null = null
let devtools: DevtoolsHandle | null = null

// Never a deployed production build: the bridge exposes per-user context (see
// `README.md`). `start:production` runs the dev server, so NODE_ENV lets it in.
function isDevtoolsEnvironment() {
  return isDaily() || isStaging() || process.env.NODE_ENV === 'development'
}

function setDevtools(agent: Agent | null) {
  const previous = devtools
  devtools = null
  try {
    previous?.detach()
    if (agent && isDevtoolsEnvironment()) {
      devtools = attachDevtoolsBridge({ agent })
    }
  } catch (error) {
    // Must not fail the turn. Not sent to Sentry: the fault is in whatever
    // installed the hook, not in the Guide.
    // eslint-disable-next-line no-console
    console.warn('[onX Guide] devtools bridge failed', error)
  }
}

function resetAgent() {
  agentPromise = null
  setDevtools(null)
}

/**
 * Caches the in-flight agent for the conversation, clearing the cache if
 * *building* it rejects (as opposed to `agent.invoke` failing on a
 * successfully built agent) — otherwise every later turn would keep replaying
 * the same rejected promise instead of asking for a fresh agent.
 *
 * Attaches the devtools bridge before returning: it learns the conversation
 * from an `invoke` middleware (RFC-0015 §5.9). A superseded build is skipped.
 */
function cacheAgent(built: Promise<Agent>): Promise<Agent> {
  const p: Promise<Agent> = built.then((agent) => {
    if (agentPromise === p) {
      setDevtools(agent)
    }
    return agent
  })
  agentPromise = p
  p.catch(() => {
    if (agentPromise === p) {
      agentPromise = null
    }
  })
  return p
}

function getAgent(
  input: MessageUser,
  convo?: ConversationStore
): Promise<Agent> {
  if (agentPromise) {
    return agentPromise
  }
  const routerFn = config.value.router
  return cacheAgent(
    routerFn ? routerFn(input, convo) : props.dependencies.getAgent()
  )
}

/**
 * Warms the cache when the card mounts, so the host's agent module (which
 * `dependencies.getAgent` pulls in via `import()`) isn't fetched on the
 * critical path of the first message. The promise cached here is the one
 * `getAgent` returns for the first turn, so nothing is built twice.
 *
 * Skipped when a router override is configured: that routes per message and
 * can't be resolved before one exists.
 */
function prefetchAgent(): void {
  if (agentPromise || config.value.router) {
    return
  }
  cacheAgent(props.dependencies.getAgent())
}

const agentRouter: AgentRouter = (input, convo) => getAgent(input, convo)

// Invalidates the cached agent whenever the configured router changes. This
// closes two gaps: `use-ai-chat-card.ts`'s `openAiChatCard` only bumps
// `conversationKey` (which resets the cache via the watcher below) when the
// card was already open and the router reference changed on reopen — a
// fresh open with a router leaves `wasOpen` false and skips that bump. And,
// more broadly, `sendPrompt`/`sendMessage` can swap the router via
// `overrides` mid-conversation without ever touching `conversationKey`, so
// without this watcher a router override after the first message would
// silently keep routing through the previously cached agent.
watch(() => config.value.router, resetAgent)

const adapters: ChatCardAdapters = {
  map: {
    showPlace: (place) =>
      props.dependencies.navigation.openPlaceDetails(place.coordinate),
    setLayerActive: (id, active) =>
      props.dependencies.layers.setLayerActive(id, active),
    getLayer: (id) => props.dependencies.layers.getLayer(id),
    openLayerDetails: (id) =>
      props.dependencies.navigation.openLayerDetails(id),
  },
  actions: {
    // The destination is intentionally not taken from the tool result: a
    // tool result must never be able to send the user to a model-chosen
    // destination, so the CTA always opens the fixed support URL.
    visitSupport: () =>
      window.open(DEFAULT_SUPPORT_URL, '_blank', 'noopener,noreferrer'),
    upgrade: () => {
      // UTM params attribute the conversion to onX Guide; the marketing
      // purchase/billing forms read these off the URL into the transaction.
      const params = new URLSearchParams({
        membership: 'elite',
        utm_source: 'onx_guide',
        utm_medium: 'ai_chat',
        utm_campaign: 'elite_upgrade',
      })
      goToPurchase(
        {
          origin: PURCHASE_CTA_ORIGINS.ONX_GUIDE,
          detail: 'Upgrade to Elite',
          action: 'Upgrade',
        },
        params.toString()
      )
    },
  },
}

const feedbackOptions: ChatCardFeedbackOptions = {
  prompt: "How did that go? We'd love to hear.",
  label: 'Send feedback',
  dismissLabel: 'Dismiss feedback prompt',
  storageKey: FEEDBACK_ENGAGEMENT_KEY,
}

const surveyOptions: ChatCardSurveyOptions = {
  heading: 'Help make onX better for everyone.',
  prompt: `How satisfied are you with the ${props.dependencies.identity.agentName} experience?`,
  ratingLabels: [
    'Very dissatisfied',
    'Somewhat dissatisfied',
    'Neutral',
    'Somewhat satisfied',
    'Very satisfied',
  ],
  commentLabel: 'Open Feedback',
  commentPlaceholder: 'Please enter comments regarding this feature',
  maxCommentLength: 500,
  cancelLabel: 'Cancel',
  submitLabel: 'Send Feedback',
  successMessage: 'Thanks for your feedback.',
  errorMessage: "Couldn't submit feedback. Please try again.",
  autoCloseMs: 1500,
}

/** Assigns everything the card doesn't already default to, on (re)mount. */
watch(
  cardRef,
  (card, previous) => {
    if (!card) {
      // `v-if` destroys this card rather than hiding it, and persistence
      // deliberately survives disconnection so a reconnected card can't have
      // newer in-memory state overwritten by a stale snapshot (RFC-0007
      // §6.22.6). Nothing reconnects a destroyed element, so clear the
      // property on the way out — that flushes the store, then detaches the
      // wrapper and its pagehide/visibilitychange listeners, without removing
      // the stored snapshot a later mount should restore from.
      if (previous) {
        previous.persistence = undefined
      }
      mountedCard = null
      return
    }
    mountedCard = card
    // Pulls the host's agent (and the tool/LLM graph its module carries) as
    // soon as the card mounts, well before any message can be sent. The
    // card's own tool-status copy comes from the toolkit's
    // `defaultToolStatusLabels`, so there's nothing to derive from it here.
    prefetchAgent()
    card.router = agentRouter
    card.renderers = renderers
    card.adapters = adapters
    card.client = getApolloClient()
    card.feedbackOptions = feedbackOptions
    card.surveyOptions = surveyOptions
    card.prompts = props.dependencies.identity.starterPrompts
    // Assigned once at mount, not re-assigned per turn: a host's
    // markdown-plugin hook reads `getConvo()` fresh on every render pass
    // `<ai-toolkit-markdown>` already performs (streaming ticks and the
    // final render alike), so it always sees the card's *current*
    // conversation without needing to be rebuilt after each turn.
    card.markdownOptions = props.dependencies.markdownOptions?.(
      () => card.convo
    )
    if (card.shadowRoot) {
      injectCitationStyles(card.shadowRoot)
    }
    // Assigned last: the setter hydrates the store from any stored
    // snapshot as a side effect, so renderers/adapters must already be
    // wired before that can happen.
    card.persistence = persistence
  },
  { immediate: true }
)

/**
 * Where the chat was *opened* from — `onx_guide_chat_opened.origin`, whose
 * catalog includes entry points like `navigation menu`.
 */
function eventOrigin() {
  return config.value.event?.origin ?? 'navigation menu'
}

/**
 * `onx_guide_message_sent.origin` is a different enumeration from
 * `onx_guide_chat_opened.origin`: it's the surface *this message* came from,
 * and the catalog admits only `chat window`, `query card`, and
 * `land area info card` — notably **not** `navigation menu`. Reusing
 * `eventOrigin()` here reported the entry point instead of the surface, so
 * every message sent after opening from the nav carried a value the event
 * doesn't define.
 *
 * A turn that a host started from a query card or land-area card reports that
 * surface, and only for the first turn (the catalog's "may fire before chat
 * opened when a user selects a starter prompt from a query card" case).
 * Everything after that is typed or sent into the mounted card, i.e. the chat
 * window. Entry points that aren't message surfaces fall through to
 * `chat window` rather than leaking an out-of-enum value.
 */
const MESSAGE_SURFACE_ORIGINS = ['query card', 'land area info card']

function messageOrigin(messageNumber: number) {
  const openedFrom = config.value.event?.origin
  return messageNumber === 1 &&
    openedFrom &&
    MESSAGE_SURFACE_ORIGINS.includes(openedFrom)
    ? openedFrom
    : 'chat window'
}

// The card only emits `close` once the built-in end-session confirmation is
// accepted (never on a bare dismiss), so it always means "discard this
// conversation" — mirrors the conversationKey watcher's clear below.
function handleClose() {
  analytics.trackChatClosed({
    total_messages: cardRef.value ? countUserTurns(cardRef.value.convo) : 0,
  })
  resetAgent()
  resetMessageContext()
  sessionStorage.removeItem(MESSAGE_CONTEXT_STORAGE_KEY)
  // `v-if` destroys the card on close rather than hiding it, so the next
  // open constructs a brand-new element — reset here (rather than leaving
  // it to the conversationKey watcher) clears the persisted snapshot too,
  // so a closed conversation doesn't come back on next open.
  cardRef.value?.reset()
  closeAiChatCard()
}

function handleCollapsedChange(e: Event) {
  const { collapsed } = (e as CustomEvent<{ collapsed: boolean }>).detail
  if (collapsed) {
    analytics.trackChatMinimized()
    return
  }
  analytics.trackChatExpanded()
}

// Label of the empty-state starter chip the user just selected, held until
// that turn settles. The card's `prompt-select` fires before it sends the
// chip's message, and `invoke-end` is where the send is tracked, so this
// carries the chip across the two. Cleared whenever the turn ends (success
// or failure) or the conversation is reset, so it only ever describes the
// turn the chip started.
let selectedStarterChip: string | null = null

// Bubbles (composed) from the card's empty state when a starter chip is
// activated; the card itself then sends the chip's message.
function handlePromptSelect(e: Event) {
  const { prompt } = (e as CustomEvent<{ prompt?: { label?: string } }>).detail
  selectedStarterChip = prompt?.label ?? null
}

// Fires for every turn origin (typed input, starter prompt, or an
// externally sent message) — the card's turn lifecycle is now unified, so
// this single handler is the one tracking call site. Persistence needs no
// call here: the card flushes its own coalesced write at the turn boundary
// (`_settleTurn`).
function handleInvokeEnd(e: Event) {
  const { convo } = (e as CustomEvent<{ convo: ConversationStore }>).detail
  const messageNumber = countUserTurns(convo)
  analytics.trackMessageSent({
    message_number: messageNumber,
    origin: messageOrigin(messageNumber),
    starter_chip:
      selectedStarterChip ?? config.value.event?.starter_chip ?? 'none',
  })
  selectedStarterChip = null
  // Persists the message-context differ's baseline alongside the
  // conversation, so a page reload mid-conversation can restore it (see the
  // `hydrateMessageContext` call in this component's top-level setup above).
  // Wrapped like the conversation's own persistence write (`persistence.onError`
  // above): a full session-storage quota is an expected failure mode here too,
  // and a partial/stale value must not linger if the write throws. Reuses the
  // same `persistenceErrorReported` latch so a repeated failure across either
  // key doesn't spam Sentry twice.
  const snapshot = snapshotMessageContext()
  try {
    if (snapshot) {
      sessionStorage.setItem(MESSAGE_CONTEXT_STORAGE_KEY, snapshot)
    } else {
      sessionStorage.removeItem(MESSAGE_CONTEXT_STORAGE_KEY)
    }
  } catch (error) {
    sessionStorage.removeItem(MESSAGE_CONTEXT_STORAGE_KEY)
    if (!persistenceErrorReported) {
      persistenceErrorReported = true
      reportAiError(error, { surface: 'persistence', layer: 'host' })
    }
  }
}

function handleInvokeError(e: Event) {
  selectedStarterChip = null
  const { error } = (e as CustomEvent<{ error: unknown }>).detail
  // A failed turn is the Guide's headline failure mode, and the bare exception
  // carries nothing to discriminate one cause from another -- how deep into the
  // conversation it died and how the turn started are what make the Sentry
  // issue actionable.
  reportAiError(error, {
    surface: 'llm-turn',
    extra: {
      messageCount: cardRef.value ? countUserTurns(cardRef.value.convo) : 0,
      origin: eventOrigin(),
    },
  })
}

// The CTA click is what opens the survey in this architecture, so both
// tracker helpers fire from the same `open-survey` trigger.
function handleOpenSurvey() {
  analytics.trackFeedbackOpened()
  analytics.trackFeedbackCtaClicked()
}

function handleFeedbackSubmit(e: Event) {
  const feedback = (e as CustomEvent<FeedbackSubmission>).detail
  analytics.trackFeedbackSubmitted({
    feedback_text: !!feedback.comment,
    rating: Number(feedback.rating),
  })
}

function handleFeedbackDismiss() {
  analytics.trackFeedbackCtaDismissed()
}

// Bubbles (composed) from `ai-toolkit-feedback-cta` whenever its computed
// visibility flips, including its first appearance. Only the appearance
// (visible === true) maps to the catalog's "shown" event -- there's no
// corresponding "hidden" event to publish.
function handleFeedbackCtaVisibleChange(e: Event) {
  const { visible } = (e as CustomEvent<{ visible: boolean }>).detail
  if (visible) {
    analytics.trackFeedbackCtaShown()
  }
}

// Bubbles from `<ai-toolkit-chat-card>` itself whenever the internal
// feedback survey's own `close` event fires -- distinct from the card's own
// `close` (end-session) event handled above. `rating` is present whenever a
// rating had been selected at the time of dismissal, submitted or not.
//
// Both `reason: 'cancel'` (the survey's own Cancel action) and
// `reason: 'close'` (a host-driven dismissal while the survey is open and
// idle) map to FEEDBACK_CANCELED -- see the naming-trap note on
// ANALYTICS_EVENTS.FEEDBACK_CLOSED in the toolkit: that event is reserved for
// a distinct in-survey close affordance `ai-toolkit-feedback-survey` doesn't
// expose, so nothing here should publish it despite the matching reason
// string. `reason: 'success'` gets neither: `handleFeedbackSubmit` above
// already publishes FEEDBACK_SUBMITTED for a successful submission.
function handleFeedbackSurveyClose(e: Event) {
  const { reason, rating } = (
    e as CustomEvent<{
      reason: 'cancel' | 'close' | 'success'
      rating?: string
    }>
  ).detail
  if (reason === 'cancel' || reason === 'close') {
    analytics.trackFeedbackCanceled(
      rating !== undefined ? { rating: Number(rating) } : undefined
    )
  }
}

// Thumbs up/down on a response (distinct from the survey's feedback-submit
// above): ai-toolkit-message-annotation submits the rating itself, this just
// confirms it landed.
function handleFeedback() {
  cardRef.value?.showToast('Thanks for your feedback!')
}

// A rating submission failure is a routine network error and the user already
// sees the toast below, so this used to stay silent. It now reports: a broken
// rating pipeline is invisible otherwise (ratings are how we measure Guide
// quality), and volume is controlled by the alert rule's threshold rather than
// by a code path that drops the signal outright.
function handleRatingError(e: Event) {
  reportAiError((e as CustomEvent<unknown>).detail, { surface: 'feedback' })
  cardRef.value?.showToast("Couldn't submit feedback. Please try again.")
}

function handleCopied() {
  cardRef.value?.showToast('Text copied')
}

watch(conversationKey, () => {
  resetAgent()
  resetMessageContext()
  sessionStorage.removeItem(MESSAGE_CONTEXT_STORAGE_KEY)
  if (cardRef.value) {
    cardRef.value.reset()
  } else {
    // Pre-mount path: opening with `clearConversation` before the card has
    // ever rendered (e.g. the first open of the session). There's no store
    // to reset yet, so clear any stale snapshot directly — the card's own
    // `persistence` assignment (cardRef watch, above) hydrates the fresh
    // store once it mounts, finding nothing to replay.
    clearPersistedConversation(CONVERSATION_STORAGE_KEY)
  }
})

watch(expandKey, async () => {
  await nextTick()
  cardRef.value?.expand()
})

watch(
  openEventKey,
  (key) => {
    if (!key || !isOpen.value) {
      return
    }
    analytics.trackChatOpened({
      origin: eventOrigin(),
      ...(config.value.event?.first_result_type
        ? { first_result_type: config.value.event.first_result_type }
        : {}),
    })
  },
  { immediate: true }
)

watch(pendingPrompt, async (template) => {
  if (!template) {
    return
  }
  consumePendingPrompt()
  await nextTick()
  const message = await template.createMessage(null)
  await cardRef.value?.sendMessage(message)
})

watch(pendingMessage, async (text) => {
  if (!text) {
    return
  }
  consumePendingMessage()
  await nextTick()
  await cardRef.value?.send(text)
})

onUnmounted(() => {
  // Not `cardRef.value` -- Vue nulls a component's template refs to its
  // children while tearing down that subtree, before running this
  // component's own `onUnmounted` callbacks, so `cardRef.value` is already
  // null here. `mountedCard` is a plain variable unaffected by that
  // teardown, so this actually reaches the still-live card and aborts its
  // in-flight request instead of silently no-op'ing.
  mountedCard?.abort()
  resetAgent()
})
</script>

<style lang="scss" scoped>
@use '@/styles/_layer-map.scss';
@use '@/styles/system/_breakpoints';

#control-ai-chat {
  --control-ai-chat-control-column-width: 50px;
  --control-ai-chat-control-gap: 30px;
  --control-ai-chat-control-bottom: 30px;

  box-sizing: border-box;
  position: absolute;
  bottom: var(--control-ai-chat-control-bottom);
  right: calc(
    var(--control-ai-chat-control-column-width) +
      var(--control-ai-chat-control-gap)
  );
  width: min(55vw, 375px);
  z-index: calc(
    layer-map.$z-index-level-card - 1
  ); // Activity card is above the chat card, so we need to be below it

  @include breakpoints.mobile {
    --control-ai-chat-control-gap: 20px;
    --control-ai-chat-control-bottom: 30px;

    bottom: var(--control-ai-chat-control-bottom);
    right: var(--control-ai-chat-control-gap);
    width: calc(100vw - var(--control-ai-chat-control-gap) * 2);
  }
}
</style>
